import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { dataService } from '../services/dataService'
import { offlineDB } from '../services/offlineDB'
import { imprimirAlbaran, imprimirResumenCliente, checkBluetoothSupport } from '../utils/printer'
import { X, Plus, Trash2, Save, Printer, FileText, Calendar, Edit, RotateCcw } from 'lucide-react'
import { calcularOrdenActual, actualizarOrdenPromedio } from '../services/ordenRutas'
import './AlbaranDialog.css'

// ── Orden visual de productos en albarán y ticket ──────────────────────────
function ordenProducto(nombre) {
  const n = (nombre || '').toLowerCase()
  if (n.includes('pan grande'))                          return 1
  if (n.includes('pan pequeño') || n.includes('pan peq')) return 2
  if (n.includes('barra')    && !n.includes('media'))    return 3
  if (n.includes('fabiola')  && !n.includes('media'))    return 4
  if (n.includes('integral') && n.includes('grande'))    return 5
  if (n.includes('media')    && n.includes('barra'))     return 6
  if (n.includes('media')    && n.includes('fabiola'))   return 7
  if (n.includes('media')    && n.includes('integral'))  return 8
  if (n.includes('bollo'))                               return 9
  if (n.includes('rosco') && !n.includes('rosquilla'))   return 10
  if (n.includes('hogaza'))                              return 11
  if (n.includes('cruasan') || n.includes('croissant'))  return 12
  if (n.includes('napolitana'))                          return 13
  if (n.includes('magdalena'))                           return 14
  if (n.includes('pan rallado'))                         return 15
  if (n.includes('saco'))                                return 16
  if (n.includes('rosquilla'))                           return 17
  if (n.includes('pasta'))                               return 18
  return 19
}
function sortProductos(arr) {
  return [...arr].sort((a, b) => {
    const d = ordenProducto(a.nombre) - ordenProducto(b.nombre)
    return d !== 0 ? d : a.nombre.localeCompare(b.nombre)
  })
}
function sortLineas(lineas, productosArr) {
  return [...lineas].sort((a, b) => {
    const pa = productosArr.find(p => p.id === a.product_id)
    const pb = productosArr.find(p => p.id === b.product_id)
    const d = ordenProducto(pa?.nombre) - ordenProducto(pb?.nombre)
    return d !== 0 ? d : (pa?.nombre || '').localeCompare(pb?.nombre || '')
  })
}

export default function AlbaranDialog({ cliente, productos, clientPrices = {}, fecha, isOnline, onClose }) {
  const [lineas, setLineas] = useState([{ product_id: '', entregado: 0, devuelto: 0 }])
  const [deliveryId, setDeliveryId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [mostrarEditorPlantilla, setMostrarEditorPlantilla] = useState(false)
  const [mostrarTotalSemanal, setMostrarTotalSemanal] = useState(false)
  const [campoActivo, setCampoActivo] = useState({ index: null, campo: null })
  const [reemplazarSiguiente, setReemplazarSiguiente] = useState(false)
  const [esLandscape, setEsLandscape] = useState(window.innerWidth >= 600)

  useEffect(() => {
    const handler = () => setEsLandscape(window.innerWidth >= 600)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const esPagoEnDia = cliente.pago_en_dia === true

  // Devuelve el PVP correcto para un producto: precio especial del cliente o precio base
  function getPrecioProducto(producto) {
    if (!producto) return 0
    return Number(clientPrices[producto.id] ?? producto.precio_base ?? 0)
  }

  useEffect(() => {
    cargarAlbaran()
    
    const timer = setTimeout(() => {
      verificarAlbaranSemanal()
    }, 500)
    
    return () => clearTimeout(timer)
  }, [])

  async function verificarAlbaranSemanal() {
    const diaSemana = new Date(fecha + 'T00:00:00').getDay()
    
    if (diaSemana === 1 && !cliente.quiere_factura && !esPagoEnDia) {
      setTimeout(() => {
        const quiereAlbaranSemanal = window.confirm(
          `Es lunes. ¿Desea emitir el albarán de cobro de la semana anterior para ${cliente.alias || cliente.razon_social}?`
        )
        
        if (quiereAlbaranSemanal) {
          setMostrarTotalSemanal(true)
        }
      }, 100)
    }
  }

  async function cargarAlbaran() {
    try {
      console.log('📄 Cargando albarán...', navigator.onLine ? 'ONLINE' : 'OFFLINE')
      
      const { data: existente } = await dataService.getDeliveryByDate(cliente.id, fecha)

      if (existente && existente.delivery_lines && existente.delivery_lines.length > 0) {
        setDeliveryId(existente.id)
        const lineasCargadas = existente.delivery_lines.map(l => ({
          product_id: l.product_id,
          entregado: l.cantidad_entregada || 0,
          devuelto: l.cantidad_devuelta || 0
        }))
        setLineas(sortLineas(lineasCargadas, productos))
        console.log('✅ Albarán existente cargado')
        return
      }

      await cargarPlantilla()
    } catch (error) {
      console.error('Error cargando albarán:', error)
      await cargarPlantilla()
    }
  }

  async function cargarPlantilla() {
    try {
      console.log('📋 Cargando plantilla...', navigator.onLine ? 'ONLINE' : 'OFFLINE')
      
      const diaSemana = new Date(fecha + 'T00:00:00').getDay()
      const tipoDia = diaSemana === 0 ? 'domingo' : diaSemana === 6 ? 'sabado' : 'laboral'
      
      const { data: plantilla } = await dataService.getClientTemplates(cliente.id)
      console.log('✅ Plantilla cargada:', plantilla?.length || 0, 'items')

      if (plantilla && plantilla.length > 0) {
        const campo = tipoDia === 'domingo' ? 'qty_domingo' : 
                      tipoDia === 'sabado' ? 'qty_sabado' : 'qty_laboral'
        
        const lineasPlantilla = plantilla
          .filter(p => p[campo] > 0)
          .map(p => ({
            product_id: p.product_id,
            entregado: p[campo],
            devuelto: 0
          }))
        
        if (lineasPlantilla.length > 0) {
          setLineas(sortLineas(lineasPlantilla, productos))
          console.log('✅ Plantilla aplicada:', lineasPlantilla.length, 'productos')
        }
      }
    } catch (error) {
      console.error('⚠️ Error cargando plantilla:', error)
    }
  }

  function agregarLinea() {
    setLineas([...lineas, { product_id: '', entregado: 0, devuelto: 0 }])
  }

  function eliminarLinea(index) {
    if (lineas.length > 1) {
      setLineas(lineas.filter((_, i) => i !== index))
    }
  }

  function actualizarLinea(index, campo, valor) {
    const nuevasLineas = [...lineas]
    nuevasLineas[index][campo] = valor
    setLineas(nuevasLineas)
  }

  function presionarTecla(tecla) {
    if (campoActivo.index === null) return
    const { index, campo } = campoActivo
    const valorActual = String(lineas[index][campo] ?? 0)
    let nuevoValor
    if (tecla === 'C') {
      nuevoValor = 0
      setReemplazarSiguiente(false)
    } else if (tecla === '⌫') {
      if (reemplazarSiguiente) {
        nuevoValor = 0
        setReemplazarSiguiente(false)
      } else {
        const sinUltimo = valorActual.slice(0, -1)
        nuevoValor = sinUltimo === '' || sinUltimo === '0.' ? 0 : sinUltimo
      }
    } else if (tecla === ',') {
      if (reemplazarSiguiente) {
        nuevoValor = '0.'
        setReemplazarSiguiente(false)
      } else if (!valorActual.includes('.')) {
        nuevoValor = valorActual + '.'
      } else {
        return  // ya tiene decimal, ignorar
      }
    } else {
      if (reemplazarSiguiente) {
        nuevoValor = Number(tecla)
        setReemplazarSiguiente(false)
      } else {
        const nuevoStr = valorActual === '0' ? tecla : valorActual + tecla
        nuevoValor = nuevoStr.includes('.') ? nuevoStr : Number(nuevoStr)
      }
    }
    actualizarLinea(index, campo, nuevoValor)
  }

  async function guardar() {
    const lineasValidas = lineas.filter(l => 
      l.product_id && l.entregado > 0
    )

    if (lineasValidas.length === 0) {
      alert('Añade al menos una línea con cantidad entregada')
      return
    }

    setLoading(true)

    try {
      console.log('💾 Guardando albarán...', navigator.onLine ? 'ONLINE' : 'OFFLINE')
      
      let ordenEntrega = null
      let horaEntrega = null
      
      if (navigator.onLine) {
        ordenEntrega = await calcularOrdenActual(fecha, cliente.ruta)
        horaEntrega = new Date().toTimeString().slice(0, 8)
        console.log(`📍 Orden de entrega: ${ordenEntrega} a las ${horaEntrega}`)
      }
      
      const lineasParaGuardar = lineasValidas.map(l => {
        const producto = productos.find(p => p.id === l.product_id)
        // ✅ Usar precio especial del cliente si existe, si no el precio base
        const precioUnit = getPrecioProducto(producto)
        return {
          product_id: l.product_id,
          cantidad_entregada: Number(l.entregado),
          cantidad_devuelta: Number(l.devuelto || 0),
          precio_unit: precioUnit,
          iva_pct: Number(producto?.iva_pct || 10),
          descuento_aplicable: producto?.descuento_aplicable || false
        }
      })

      const result = await dataService.saveDelivery({
        client_id: cliente.id,
        fecha: fecha,
        ruta: cliente.ruta,
        lines: lineasParaGuardar,
        orden_entrega: ordenEntrega,
        hora_entrega: horaEntrega
      }, deliveryId)

      if (result.success && navigator.onLine && ordenEntrega) {
        actualizarOrdenPromedio(cliente.id, fecha).catch(err => 
          console.warn('⚠️ No se pudo actualizar orden promedio:', err)
        )
      }

      if (result.success) {
        const accion = result.isUpdate ? 'actualizado' : 'guardado'
        const mensaje = result.mode === 'offline' 
          ? `Reparto ${accion} OFFLINE. Se sincronizará cuando haya conexión.`
          : `Reparto ${accion} correctamente.`
        
        const mensajeFinal = esPagoEnDia
          ? `${mensaje}\n\n💵 Pago en día - el ticket incluirá precios y total\n\n¿Desea imprimirlo?`
          : `${mensaje}\n\n¿Desea imprimirlo?`

        const quiereImprimir = window.confirm(mensajeFinal)
        onClose()
        if (quiereImprimir) {
          await handleImprimir(lineasValidas)
        }
      } else {
        throw new Error('Error al guardar')
      }
    } catch (error) {
      console.error('❌ Error guardando:', error)
      alert('Error al guardar: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleImprimir(lineasParaImprimir = null) {
    const bluetoothCheck = checkBluetoothSupport()
    if (!bluetoothCheck.supported) {
      alert(bluetoothCheck.message)
      return
    }

    try {
      const lineasAImprimir = lineasParaImprimir || lineas.filter(l => l.product_id && (l.entregado > 0 || l.devuelto > 0))
      
      if (lineasAImprimir.length === 0) {
        alert('No hay líneas para imprimir')
        return
      }

      const conPrecios = esPagoEnDia
      await imprimirAlbaran(cliente, lineasAImprimir, productos, fecha, conPrecios)
    } catch (error) {
      console.error('Error imprimiendo:', error)
      alert('Error al imprimir: ' + error.message)
    }
  }

  async function eliminarAlbaran() {
    if (!deliveryId) {
      alert('No hay albarán para eliminar')
      return
    }

    if (!confirm('¿Seguro que quieres borrar este albarán?')) return

    try {
      if (navigator.onLine) {
        await supabase.from('delivery_lines').delete().eq('delivery_id', deliveryId)
        await supabase.from('deliveries').delete().eq('id', deliveryId)
        alert('Albarán eliminado')
      } else {
        alert('No se puede eliminar en modo offline')
      }
      onClose()
    } catch (error) {
      alert('Error al eliminar: ' + error.message)
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <div>
            <h2>
              Albarán - {cliente.alias || cliente.razon_social}
              {esPagoEnDia && (
                <span style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.75rem',
                  background: 'rgba(245, 158, 11, 0.2)',
                  color: '#f59e0b',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  padding: '0.125rem 0.5rem',
                  verticalAlign: 'middle'
                }}>
                  💶 Pago en día
                </span>
              )}
            </h2>
            <p className="dialog-subtitle">
              Fecha: {fecha} · Ruta: {cliente.ruta}
              {!isOnline && <span style={{ color: 'var(--warn)', marginLeft: '0.5rem', fontWeight: 'bold' }}>• MODO OFFLINE</span>}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={guardar} disabled={loading} className="btn-primary" style={{ padding: '0.625rem 1rem', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Save size={18} /> {loading ? 'Guardando...' : 'Guardar'}
            </button>
            <button className="btn-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={{ padding: '0 1rem 0.75rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', borderBottom: '1px solid var(--line)', alignItems: 'center' }}>
          {/* Cargar plantilla: solo icono refresh */}
          <button onClick={cargarPlantilla} className="btn-secondary" title="Cargar plantilla" style={{ padding: '0.5rem 0.625rem' }}>
            <RotateCcw size={16} />
          </button>
          <button onClick={() => setMostrarEditorPlantilla(true)} className="btn-secondary" title="Editar plantilla" style={{ fontSize: '0.8rem', padding: '0.5rem 0.625rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Edit size={15} /> Editar plantilla
          </button>
          {!esPagoEnDia && (
            <button onClick={() => setMostrarTotalSemanal(true)} className="btn-secondary" title="Total semana" style={{ fontSize: '0.8rem', padding: '0.5rem 0.625rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Calendar size={15} /> Total semana
            </button>
          )}
          <button onClick={agregarLinea} className="btn-secondary" title="Añadir producto" style={{ fontSize: '0.8rem', padding: '0.5rem 0.625rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Plus size={15} /> Añadir producto
          </button>
        </div>

        <div className="dialog-body-flex">
          <div className="lineas-panel">
            <div className="tabla-wrapper">
              <table className="tabla-lineas">
                <thead>
                  <tr>
                    <th style={{width: '65px', textAlign: 'center'}}>Entregado</th>
                    <th>Producto</th>
                    <th style={{width: '65px', textAlign: 'center'}}>Dev.</th>
                    <th style={{width: '36px'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((linea, index) => (
                    <tr key={index}>
                      <td>
                        <input type="number" min="0" step="any" value={linea.entregado}
                          onChange={(e) => actualizarLinea(index, 'entregado', e.target.value)}
                          onPointerDown={() => {
                            setCampoActivo({ index, campo: 'entregado' })
                            setReemplazarSiguiente(true)
                          }}
                          onFocus={(e) => {
                            setCampoActivo({ index, campo: 'entregado' })
                            setReemplazarSiguiente(true)
                            e.target.select()
                          }}
                          className={`input-cantidad${campoActivo.index === index && campoActivo.campo === 'entregado' ? ' input-activo' : ''}`} />
                      </td>
                      <td>
                        <select value={linea.product_id}
                          onChange={(e) => actualizarLinea(index, 'product_id', e.target.value)}
                          className="select-producto">
                          <option value="">— elegir —</option>
                          {sortProductos(productos
                            .filter(p => !lineas.some((l, i) => i !== index && l.product_id === p.id)))
                            .map(p => (
                              <option key={p.id} value={p.id}>
                                {p.nombre} - {getPrecioProducto(p).toFixed(2)}€
                                {clientPrices[p.id] ? ' ★' : ''}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        <input type="number" min="0" step="any" value={linea.devuelto}
                          onChange={(e) => actualizarLinea(index, 'devuelto', e.target.value)}
                          onPointerDown={() => {
                            setCampoActivo({ index, campo: 'devuelto' })
                            setReemplazarSiguiente(true)
                          }}
                          onFocus={(e) => {
                            setCampoActivo({ index, campo: 'devuelto' })
                            setReemplazarSiguiente(true)
                            e.target.select()
                          }}
                          className={`input-cantidad${campoActivo.index === index && campoActivo.campo === 'devuelto' ? ' input-activo' : ''}`} />
                      </td>
                      <td>
                        {lineas.length > 1 && (
                          <button onClick={() => eliminarLinea(index)} className="btn-icon-danger">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button onClick={agregarLinea} className="btn-add-linea">
              <Plus size={18} /> Añadir línea
            </button>
          </div>

          {esLandscape && (
            <div className="teclado-panel">
              <TecladoNumerico
                onTecla={presionarTecla}
                campoActivo={campoActivo}
              />
            </div>
          )}
        </div>


        <div className="dialog-footer">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          {deliveryId && navigator.onLine && (
            <button onClick={eliminarAlbaran} className="btn-danger">
              <Trash2 size={18} /> Borrar
            </button>
          )}
          <button onClick={() => handleImprimir()} className="btn-secondary">
            <Printer size={18} /> Imprimir
          </button>
        </div>
      </div>

      {mostrarEditorPlantilla && (
        <EditorPlantilla 
          cliente={cliente}
          productos={productos}
          onClose={() => setMostrarEditorPlantilla(false)}
          onGuardar={() => {
            setMostrarEditorPlantilla(false)
            cargarPlantilla()
          }}
        />
      )}

      {mostrarTotalSemanal && (
        <TotalSemanal
          cliente={cliente}
          productos={productos}
          clientPrices={clientPrices}
          fechaReferencia={fecha}
          onClose={() => setMostrarTotalSemanal(false)}
        />
      )}
    </div>
  )
}

function TecladoNumerico({ onTecla, campoActivo }) {
  // 2 columnas en móvil landscape; C va a ancho completo abajo
  const filas = [['1','2'], ['3','4'], ['5','6'], ['7','8'], ['9','0'], [',','⌫']]
  const sinFoco = campoActivo.index === null
  const labelCampo = campoActivo.campo === 'entregado' ? '✏️ Entregado'
    : campoActivo.campo === 'devuelto' ? '↩️ Devolución'
    : 'Toca un campo'
  return (
    <div className="teclado-numerico">
      <div className="teclado-label">{labelCampo}</div>
      <div className="teclado-grid teclado-grid-2col">
        {filas.map((fila, i) =>
          fila.map((tecla, j) => (
            <button
              key={`${i}-${j}`}
              className={`teclado-btn${tecla === '⌫' ? ' teclado-btn-back' : ''}${tecla === ',' ? ' teclado-btn-comma' : ''}`}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => onTecla(tecla)}
              disabled={sinFoco}
            >
              {tecla}
            </button>
          ))
        )}
      </div>
      <button
        className="teclado-btn teclado-btn-clear teclado-btn-full"
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => onTecla('C')}
        disabled={sinFoco}
      >
        C
      </button>
    </div>
  )
}

function EditorPlantilla({ cliente, productos, onClose, onGuardar }) {
  const [plantilla, setPlantilla] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    cargarPlantillaActual()
  }, [])

  async function cargarPlantillaActual() {
    try {
      const { data } = await dataService.getClientTemplates(cliente.id)
      const plantillaMap = {}
      data?.forEach(item => {
        plantillaMap[item.product_id] = {
          qty_laboral: item.qty_laboral || 0,
          qty_sabado: item.qty_sabado || 0,
          qty_domingo: item.qty_domingo || 0
        }
      })
      setPlantilla(plantillaMap)
    } catch (error) {
      console.error('Error cargando plantilla:', error)
    } finally {
      setLoading(false)
    }
  }

  function actualizarCantidad(productId, campo, valor) {
    setPlantilla(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || { qty_laboral: 0, qty_sabado: 0, qty_domingo: 0 }),
        [campo]: Number(valor) || 0
      }
    }))
  }

  async function guardarPlantilla() {
    setLoading(true)
    try {
      const registros = []
      Object.keys(plantilla).forEach(productId => {
        const p = plantilla[productId]
        if (p.qty_laboral > 0 || p.qty_sabado > 0 || p.qty_domingo > 0) {
          registros.push({
            client_id: cliente.id,
            product_id: productId,
            qty_laboral: p.qty_laboral || 0,
            qty_sabado: p.qty_sabado || 0,
            qty_domingo: p.qty_domingo || 0,
            activo: true
          })
        }
      })
      const result = await dataService.saveClientTemplates(cliente.id, registros)
      const mensaje = result.mode === 'offline'
        ? 'Plantilla guardada OFFLINE. Se sincronizará cuando haya conexión.'
        : 'Plantilla guardada correctamente.'
      alert(mensaje)
      onGuardar()
    } catch (error) {
      alert('Error al guardar plantilla: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose} style={{zIndex: 1001}}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '800px'}}>
        <div className="dialog-header">
          <div>
            <h2>Editar Plantilla - {cliente.alias || cliente.razon_social}</h2>
            <p className="dialog-subtitle">Define las cantidades por tipo de día</p>
          </div>
          <button className="btn-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="dialog-body">
          {loading ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>Cargando...</div>
          ) : (
            <div className="tabla-wrapper">
              <table className="tabla-lineas">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th style={{width: '100px', textAlign: 'center'}}>L-V</th>
                    <th style={{width: '100px', textAlign: 'center'}}>Sábado</th>
                    <th style={{width: '100px', textAlign: 'center'}}>Domingo</th>
                  </tr>
                </thead>
                <tbody>
                  {sortProductos(productos).map(producto => {
                    const cant = plantilla[producto.id] || { qty_laboral: 0, qty_sabado: 0, qty_domingo: 0 }
                    return (
                      <tr key={producto.id}>
                        <td>{producto.nombre}</td>
                        <td><input type="number" min="0" step="1" value={cant.qty_laboral}
                          onChange={(e) => actualizarCantidad(producto.id, 'qty_laboral', e.target.value)}
                          className="input-cantidad" /></td>
                        <td><input type="number" min="0" step="1" value={cant.qty_sabado}
                          onChange={(e) => actualizarCantidad(producto.id, 'qty_sabado', e.target.value)}
                          className="input-cantidad" /></td>
                        <td><input type="number" min="0" step="1" value={cant.qty_domingo}
                          onChange={(e) => actualizarCantidad(producto.id, 'qty_domingo', e.target.value)}
                          className="input-cantidad" /></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="dialog-footer">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={guardarPlantilla} disabled={loading} className="btn-primary">
            <Save size={18} /> Guardar plantilla
          </button>
        </div>
      </div>
    </div>
  )
}

function TotalSemanal({ cliente, productos, clientPrices = {}, fechaReferencia, onClose }) {
  const [fechaDesde, setFechaDesde] = useState('')
  const [fechaHasta, setFechaHasta] = useState('')
  const [resumen, setResumen] = useState([])
  const [totalConMargen, setTotalConMargen] = useState(0)
  const [totalSinMargen, setTotalSinMargen] = useState(0)
  const [montoMargen, setMontoMargen] = useState(0)
  const [totalFinal, setTotalFinal] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    calcularSemanaAnterior()
  }, [])

  function calcularSemanaAnterior() {
    const hoy = new Date(fechaReferencia + 'T12:00:00')
    const diaSemanaHoy = hoy.getDay()
    
    let diasHastaLunesPasado
    if (diaSemanaHoy === 1) {
      diasHastaLunesPasado = 7
    } else if (diaSemanaHoy === 0) {
      diasHastaLunesPasado = 6
    } else {
      diasHastaLunesPasado = diaSemanaHoy - 1 + 7
    }
    
    const lunesPasado = new Date(hoy)
    lunesPasado.setDate(hoy.getDate() - diasHastaLunesPasado)
    
    const domingoPasado = new Date(lunesPasado)
    domingoPasado.setDate(lunesPasado.getDate() + 6)

    setFechaDesde(lunesPasado.toISOString().slice(0, 10))
    setFechaHasta(domingoPasado.toISOString().slice(0, 10))
  }

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      cargarResumen()
    }
  }, [fechaDesde, fechaHasta])

  async function cargarResumen() {
    setLoading(true)
    try {
      let deliveries = []

      if (navigator.onLine) {
        try {
          const { data, error } = await supabase
            .from('deliveries')
            .select('id, fecha, delivery_lines(product_id, cantidad_entregada, precio_unit, descuento_aplicable)')
            .eq('client_id', cliente.id)
            .gte('fecha', fechaDesde)
            .lte('fecha', fechaHasta)

          if (!error && data) deliveries = data
        } catch (err) {
          console.log('⚠️ Exception online:', err.message)
        }
      }

      if (deliveries.length === 0) {
        const albaranesOffline = await offlineDB.getAll('deliveries_offline')
        const clienteIdStr = String(cliente.id)
        const albaranesFiltrados = albaranesOffline.filter(d => {
          return String(d.client_id) === clienteIdStr && d.fecha >= fechaDesde && d.fecha <= fechaHasta
        })
        deliveries = albaranesFiltrados.map(d => ({
          id: d.id,
          fecha: d.fecha,
          delivery_lines: (d.lines || []).map(l => ({
            product_id: l.product_id,
            cantidad_entregada: l.cantidad_entregada,
            precio_unit: l.precio_unit,
            descuento_aplicable: l.descuento_aplicable
          }))
        }))
      }

      if (deliveries.length === 0) {
        setResumen([])
        setTotalConMargen(0)
        setTotalSinMargen(0)
        setMontoMargen(0)
        setTotalFinal(0)
        setLoading(false)
        return
      }

      const productosMap = new Map(productos.map(p => [p.id, p]))
      const resumenPorProducto = {}
      let subtotalConMargen = 0
      let subtotalSinMargen = 0

      deliveries.forEach(delivery => {
        delivery.delivery_lines.forEach(linea => {
          const producto = productosMap.get(linea.product_id)
          if (!producto) return

          if (!resumenPorProducto[linea.product_id]) {
            // ✅ Usar precio especial del cliente si existe
            const precioUnit = Number(clientPrices[linea.product_id] ?? linea.precio_unit ?? 0)
            resumenPorProducto[linea.product_id] = {
              nombre: producto.nombre,
              cantidad: 0,
              precioUnit: precioUnit,
              subtotal: 0,
              aplicaDescuento: linea.descuento_aplicable
            }
          }

          const cantidad = Number(linea.cantidad_entregada || 0)
          const precio = resumenPorProducto[linea.product_id].precioUnit
          const subtotal = cantidad * precio
          
          resumenPorProducto[linea.product_id].cantidad += cantidad
          resumenPorProducto[linea.product_id].subtotal += subtotal

          if (linea.descuento_aplicable) {
            subtotalConMargen += subtotal
          } else {
            subtotalSinMargen += subtotal
          }
        })
      })

      const descuentoCliente = Number(cliente.descuento_porcentaje || 0) / 100
      const montoDescuento = subtotalConMargen * descuentoCliente
      const total = (subtotalConMargen - montoDescuento) + subtotalSinMargen

      setResumen(Object.values(resumenPorProducto))
      setTotalConMargen(subtotalConMargen)
      setTotalSinMargen(subtotalSinMargen)
      setMontoMargen(montoDescuento)
      setTotalFinal(total)
    } catch (error) {
      alert('Error al cargar resumen: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  async function imprimirResumen() {
    const bluetoothCheck = checkBluetoothSupport()
    if (!bluetoothCheck.supported) {
      alert(bluetoothCheck.message)
      return
    }

    try {
      const usarAncho = confirm(
        '¿En qué formato desea imprimir?\n\nAceptar = 58mm (estrecho)\nCancelar = 80mm (ancho)'
      )
      const formato = usarAncho ? '58mm' : '80mm'

      await imprimirResumenCliente(
        cliente, resumen, totalConMargen, totalSinMargen,
        montoMargen, totalFinal, fechaDesde, fechaHasta,
        formato
      )
    } catch (error) {
      alert('Error al imprimir: ' + error.message)
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose} style={{zIndex: 1001}}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '700px'}}>
        <div className="dialog-header">
          <div>
            <h2>Total Semana - {cliente.alias || cliente.razon_social}</h2>
            <p className="dialog-subtitle">Resumen de ventas del período</p>
          </div>
          <button className="btn-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="dialog-body">
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{display: 'flex', gap: '1rem'}}>
            <div style={{flex: 1}}>
              <label style={{display: 'block', fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem'}}>Desde</label>
              <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                style={{width: '100%', padding: '0.5rem', background: '#0e1116', border: '1px solid var(--line)', borderRadius: '6px', color: 'var(--fg)', colorScheme: 'dark'}} />
            </div>
            <div style={{flex: 1}}>
              <label style={{display: 'block', fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem'}}>Hasta</label>
              <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                style={{width: '100%', padding: '0.5rem', background: '#0e1116', border: '1px solid var(--line)', borderRadius: '6px', color: 'var(--fg)', colorScheme: 'dark'}} />
            </div>
          </div>

          {loading ? (
            <div style={{textAlign: 'center', padding: '2rem'}}>Cargando...</div>
          ) : (
            <div className="tabla-wrapper">
              <table className="tabla-lineas">
                <thead>
                  <tr>
                    <th style={{width: '80px', textAlign: 'center'}}>Cantidad</th>
                    <th>Producto</th>
                    <th style={{width: '100px', textAlign: 'right'}}>Precio/ud</th>
                    <th style={{width: '100px', textAlign: 'right'}}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{textAlign: 'center', color: 'var(--muted)', padding: '2rem'}}>
                        No hay ventas en este período
                      </td>
                    </tr>
                  ) : (
                    <>
                      {resumen.filter(item => item.aplicaDescuento).map((item, index) => (
                        <tr key={`con-${index}`}>
                          <td style={{textAlign: 'center', fontWeight: 'bold'}}>{item.cantidad}</td>
                          <td>{item.nombre}</td>
                          <td style={{textAlign: 'right', fontFamily: 'monospace'}}>{item.precioUnit.toFixed(2)}€</td>
                          <td style={{textAlign: 'right', fontWeight: 'bold'}}>{item.subtotal.toFixed(2)}€</td>
                        </tr>
                      ))}
                      
                      {cliente.descuento_porcentaje > 0 && totalConMargen > 0 && (
                        <tr style={{backgroundColor: 'rgba(239, 68, 68, 0.1)'}}>
                          <td></td>
                          <td colSpan="2" style={{fontStyle: 'italic', color: 'var(--warn)'}}>
                            Descuento {cliente.descuento_porcentaje}%
                          </td>
                          <td style={{textAlign: 'right', color: 'var(--warn)', fontWeight: 'bold'}}>
                            -{montoMargen.toFixed(2)}€
                          </td>
                        </tr>
                      )}

                      {resumen.filter(item => !item.aplicaDescuento).map((item, index) => (
                        <tr key={`sin-${index}`}>
                          <td style={{textAlign: 'center', fontWeight: 'bold'}}>{item.cantidad}</td>
                          <td>{item.nombre}</td>
                          <td style={{textAlign: 'right', fontFamily: 'monospace'}}>{item.precioUnit.toFixed(2)}€</td>
                          <td style={{textAlign: 'right', fontWeight: 'bold'}}>{item.subtotal.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{borderTop: '2px solid var(--line)'}}>
                    <td colSpan="3" style={{textAlign: 'right', paddingTop: '0.75rem', fontWeight: 'bold', fontSize: '1.125rem'}}>
                      TOTAL A PAGAR:
                    </td>
                    <td style={{textAlign: 'right', paddingTop: '0.75rem', fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--accent)'}}>
                      {totalFinal.toFixed(2)}€
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          </div>
        </div>

        <div className="dialog-footer">
          <button onClick={onClose} className="btn-secondary">Cerrar</button>
          <button onClick={imprimirResumen} disabled={resumen.length === 0} className="btn-primary">
            <Printer size={18} /> Imprimir resumen
          </button>
        </div>
      </div>
    </div>
  )
}
